import { ClockCircleOutlined, CloseCircleFilled, EditOutlined, UserOutlined } from "@ant-design/icons";
import { Checkbox, Input, InputNumber, Modal, Segmented, Tag, Tooltip, message } from "antd";
import type { TableColumnsType } from "antd";
import { customerApi } from "api/customer.api";
import { noteApi } from "api/note.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import { TableContent, TableLayout, TableLayoutFooter, getTableShowingRange } from "components/table";
import { fetchStaffOptions, useRemoteOptions } from "hooks/useRemoteOptions";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { userStore } from "store/userStore";
import { Customer } from "types/customer";
import { NoteRefType } from "types/note";
import { QueryParam } from "types/query";
import { getTitle } from "utils";
import { can } from "utils/auth";
import { formatDate } from "utils/date";
import { customerIndustryOptions } from "views/Customer/customer.meta";
import "views/Customer/Customer.scss";

const CUSTOMER_PATH = "/customer";
const MAX_INTERVAL = 30;

interface ReminderRow {
  customer: Customer;
  lastInteractionAt: number;
  daysSince: number;
  interval: number;
  isCustomInterval: boolean;
  overdueDays: number;
}

// Ô chu kỳ tương tác sửa trực tiếp trên dòng (1-30 ngày, để trống = dùng mặc định)
const IntervalCell = ({
  row,
  defaultInterval,
  disabled,
  onSaved,
}: {
  row: ReminderRow;
  defaultInterval: number;
  disabled: boolean;
  onSaved: () => void;
}) => {
  const initial = row.isCustomInterval ? row.interval : null;
  const [draft, setDraft] = useState<number | null>(initial);
  useEffect(() => setDraft(initial), [initial]);

  const save = async () => {
    if (draft === initial) return;
    try {
      await customerApi.updateContactInterval(row.customer.id, { contactIntervalDays: draft ?? 0 });
      message.success(draft ? `Đã đặt chu kỳ ${draft} ngày` : `Đã về chu kỳ mặc định (${defaultInterval} ngày)`);
      onSaved();
    } catch {
      setDraft(initial);
    }
  };

  return (
    <Tooltip title={`Số ngày cần tương tác (tối đa ${MAX_INTERVAL}). Để trống = mặc định ${defaultInterval} ngày`}>
      <InputNumber
        min={1}
        max={MAX_INTERVAL}
        precision={0}
        size="small"
        style={{ width: 90 }}
        placeholder={String(defaultInterval)}
        addonAfter="ngày"
        value={draft}
        disabled={disabled}
        onChange={(value) => setDraft(value === null ? null : Math.round(Number(value)))}
        onBlur={save}
        onPressEnter={save}
      />
    </Tooltip>
  );
};

export const CustomerReminderPage = observer(({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<QueryParam>({ page: 1, limit: 50, search: "", sortBy: "FARTHEST" });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ReminderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [defaultInterval, setDefaultInterval] = useState(14);
  const [noteTarget, setNoteTarget] = useState<Customer>();
  const [noteContent, setNoteContent] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);

  const onlyMine = !userStore.info.role?.isAdmin && can(CUSTOMER_PATH, "ONLY_MINE");
  const canUpdate = can(CUSTOMER_PATH, "UPDATE");
  const staffFilter = useRemoteOptions(fetchStaffOptions, !onlyMine);

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  const fetchData = useCallback(async (queryParams: QueryParam) => {
    setLoading(true);
    try {
      const res = await customerApi.findReminders(queryParams);
      setData(res.data.reminders || []);
      setTotal(res.data.total || 0);
      setDefaultInterval(res.data.defaultInterval || 14);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(query);
  }, [fetchData, query]);

  const refresh = useCallback(() => fetchData(query), [fetchData, query]);

  const debounceSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        setQuery((prev) => ({ ...prev, search: keyword, page: 1 }));
      }, 300),
    [],
  );
  useEffect(() => () => debounceSearch.cancel(), [debounceSearch]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      debounceSearch(value);
    },
    [debounceSearch],
  );

  const handleClearSearch = useCallback(() => {
    setSearch("");
    debounceSearch.cancel();
    setQuery((prev) => ({ ...prev, page: 1, search: "" }));
  }, [debounceSearch]);

  const handlePageChange = useCallback(({ limit, page }: { limit: number; page: number }) => {
    setQuery((prev) => ({ ...prev, page, limit }));
  }, []);

  const setQueryFilter = useCallback((key: string, value?: string | number | boolean) => {
    setQuery((prev) => {
      const next: QueryParam = { ...prev, page: 1 };
      if (value) {
        next[key] = value;
      } else {
        delete next[key];
      }
      return next;
    });
  }, []);

  const filters = useMemo(
    () => [
      {
        key: "industry",
        value: (query.industry as string | undefined) ?? "",
        options: customerIndustryOptions,
        onChange: (value: string | number | undefined) => setQueryFilter("industry", value ? String(value) : undefined),
        placeholder: "Ngành nghề",
        allowClear: true,
        width: 190,
      },
      ...(onlyMine
        ? []
        : [
          {
            key: "staffId",
            value: (query.staffId as number | undefined) ?? "",
            options: staffFilter.options,
            onChange: (value: string | number | undefined) => {
              const staffId = typeof value === "number" ? value : undefined;
              staffFilter.select(staffId);
              setQueryFilter("staffId", staffId);
            },
            placeholder: "Sale chăm sóc",
            allowClear: true,
            showSearch: true,
            filterOption: false as const,
            onSearch: staffFilter.onSearch,
            width: 200,
          },
        ]),
    ],
    [onlyMine, query.industry, query.staffId, setQueryFilter, staffFilter],
  );

  const openNote = useCallback((customer: Customer) => {
    setNoteContent("");
    setNoteTarget(customer);
  }, []);

  const saveNote = async () => {
    const content = noteContent.trim();
    if (!content || !noteTarget) {
      message.warning("Vui lòng nhập nội dung ghi chú");
      return;
    }
    setNoteSaving(true);
    try {
      await noteApi.create({ note: { content, refType: NoteRefType.Customer, refId: noteTarget.id } });
      message.success("Đã ghi chú, khách được đẩy xuống cuối danh sách");
      setNoteTarget(undefined);
      await refresh();
    } finally {
      setNoteSaving(false);
    }
  };

  const columns = useMemo<TableColumnsType<ReminderRow>>(
    () => [
      {
        title: "Khách hàng",
        key: "customer",
        render: (_, row) => (
          <div>
            <Link to={`/customer?customerId=${row.customer.id}`} className="font-semibold">
              {row.customer.name || "--"}
            </Link>
            <div className="text-xs text-gray-500">{row.customer.code}</div>
          </div>
        ),
      },
      {
        title: "Sale chăm sóc",
        key: "staff",
        width: 160,
        render: (_, row) => <TableContent>{row.customer.staff?.name || "--"}</TableContent>,
      },
      {
        title: "Tương tác gần nhất",
        key: "lastInteractionAt",
        width: 170,
        render: (_, row) => (
          <div>
            <TableContent>{formatDate(row.lastInteractionAt)}</TableContent>
            <div className="text-xs text-gray-500">{row.daysSince === 0 ? "Hôm nay" : `${row.daysSince} ngày trước`}</div>
          </div>
        ),
      },
      {
        title: "Ghi chú gần nhất",
        key: "lastNote",
        width: 360,
        render: (_, row) => {
          const note = row.customer.lastNote;
          if (!note) return <TableContent>--</TableContent>;
          const isSystem = note.type === "SYSTEM";
          return (
            <Tooltip title={<span style={{ whiteSpace: "pre-wrap" }}>{note.content}</span>}>
              <div className={`customerLastNote${isSystem ? " customerLastNote--system" : ""}`}>
                <div className="customerLastNote__content">{note.content}</div>
                <div className="customerLastNote__meta">
                  {isSystem && <span className="customerLastNote__badge">Hệ thống</span>}
                  {note.staffName && (
                    <span className="customerLastNote__metaItem">
                      <UserOutlined /> {note.staffName}
                    </span>
                  )}
                  {note.createdAt > 0 && (
                    <span className="customerLastNote__metaItem">
                      <ClockCircleOutlined /> {formatDate(note.createdAt)}
                    </span>
                  )}
                </div>
              </div>
            </Tooltip>
          );
        },
      },
      {
        title: "Chu kỳ tương tác",
        key: "interval",
        width: 150,
        render: (_, row) => (
          <IntervalCell row={row} defaultInterval={defaultInterval} disabled={!canUpdate} onSaved={refresh} />
        ),
      },
      {
        title: "Trạng thái",
        key: "status",
        width: 160,
        render: (_, row) =>
          row.overdueDays > 0 ? (
            <Tag color="red">Quá hạn {row.overdueDays} ngày</Tag>
          ) : (
            <Tag color="green">Còn {Math.max(0, -row.overdueDays)} ngày</Tag>
          ),
      },
      {
        title: "Thao tác",
        key: "action",
        width: 140,
        align: "center" as const,
        render: (_, row) => (
          <AppButton icon={<EditOutlined />} onClick={() => openNote(row.customer)}>
            Ghi chú nhanh
          </AppButton>
        ),
      },
    ],
    [canUpdate, defaultInterval, openNote, refresh],
  );

  const { showingFrom, showingTo } = getTableShowingRange(query.page, query.limit, total);

  return (
    <div>
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              width: 380,
              placeholder: "Tìm theo tên, mã khách, người liên hệ",
              onChange: handleSearchChange,
              inputProps: {
                allowClear: {
                  clearIcon: <CloseCircleFilled onClick={handleClearSearch} />,
                },
              },
            }}
            leftActions={
              <>
                <Segmented
                  value={(query.sortBy as string) || "FARTHEST"}
                  onChange={(value) => setQueryFilter("sortBy", String(value))}
                  options={[
                    { label: "Xa nhất", value: "FARTHEST" },
                    { label: "Quá hạn nhiều nhất", value: "MOST_OVERDUE" },
                  ]}
                />
                <Checkbox
                  checked={!!query.onlyOverdue}
                  onChange={(event) => setQueryFilter("onlyOverdue", event.target.checked ? "true" : undefined)}
                >
                  Chỉ quá hạn
                </Checkbox>
              </>
            }
            filterLabel={null}
            filters={filters}
          />
        }
        table={
          <AppTable<ReminderRow>
            rowKey={(row) => row.customer.id}
            loading={loading}
            pagination={false}
            dataSource={data}
            columns={columns}
            locale={{ emptyText: "Không có khách hàng cần nhắc" }}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={handlePageChange}
          />
        }
      />

      <Modal
        open={!!noteTarget}
        title={`Ghi chú nhanh${noteTarget ? ` - ${noteTarget.name}` : ""}`}
        okText="Lưu ghi chú"
        cancelText="Hủy"
        confirmLoading={noteSaving}
        onOk={saveNote}
        onCancel={() => setNoteTarget(undefined)}
        destroyOnClose
      >
        <Input.TextArea
          autoFocus
          rows={5}
          value={noteContent}
          onChange={(event) => setNoteContent(event.target.value)}
          placeholder="Nội dung trao đổi với khách (gọi điện, gặp mặt, nhắn Zalo...)"
        />
      </Modal>
    </div>
  );
});
